import React from 'react';
import { FunctionSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  PAYROLL_FORMULA_PRESETS,
  PAYROLL_FORMULA_VARIABLES,
  evaluatePayrollFormula,
} from '@/lib/payroll/payrollFormulas';
import type { PayrollCalculatorEditableRow } from '@/lib/api/accounting';

export type FormulaApplyScope = 'all' | number;

type PayrollFormulaBarProps = {
  projectOptions: Array<{ id: number; label: string }>;
  rows: PayrollCalculatorEditableRow[];
  onApply: (formula: string, scope: FormulaApplyScope) => void;
};

const PayrollFormulaBar: React.FC<PayrollFormulaBarProps> = ({ projectOptions, rows, onApply }) => {
  const [formula, setFormula] = React.useState(PAYROLL_FORMULA_PRESETS[0]?.formula ?? '');
  const [scope, setScope] = React.useState<string>('all');
  const [error, setError] = React.useState('');

  const tryApply = () => {
    setError('');
    const trimmed = formula.trim();
    if (!trimmed) {
      setError('فرمول را وارد کنید.');
      return;
    }
    const scopeId = scope === 'all' ? 'all' : Number(scope);
    const filter =
      scopeId === 'all'
        ? undefined
        : (row: PayrollCalculatorEditableRow) => row.project_id === scopeId;
    const sample = rows.find((r) => (filter ? filter(r) : true));
    if (!sample) {
      setError('ردیفی برای اعمال فرمول در محدوده انتخاب‌شده نیست.');
      return;
    }
    try {
      evaluatePayrollFormula(trimmed, sample);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'فرمول نامعتبر است.');
      return;
    }
    onApply(trimmed, scopeId === 'all' ? 'all' : scopeId);
  };

  return (
    <Card className="border-violet-200 bg-violet-50/40">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <FunctionSquare className="h-5 w-5 text-violet-700" />
          فرمول محاسبه خالص (مثل اکسل)
        </CardTitle>
        <CardDescription>
          فرمول خالص پرداخت را مثل اکسل بنویسید؛ مثلاً{' '}
          <code className="rounded bg-white px-1 font-mono text-violet-900" dir="ltr">
            present_days * daily_salary
          </code>{' '}
          یا{' '}
          <code className="rounded bg-white px-1 font-mono text-violet-900" dir="rtl">
            تعداد حضور * دستمزد روز
          </code>
          . می‌توانید از <strong>ضرب</strong> یا <strong>×</strong> هم استفاده کنید. حقوق پایه و سایر
          مبالغ در جدول قابل ویرایش‌اند و در فرمول لحاظ می‌شوند.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {PAYROLL_FORMULA_PRESETS.map((p) => (
            <Button
              key={p.id}
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => {
                setFormula(p.formula);
                setError('');
              }}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_11rem_auto]">
          <Input
            value={formula}
            onChange={(e) => {
              setFormula(e.target.value);
              setError('');
            }}
            dir="ltr"
            className="font-mono text-sm"
            placeholder="present_days * daily_salary"
          />
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">همه پروژه‌ها</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.label}
              </option>
            ))}
          </select>
          <Button type="button" onClick={tryApply}>
            اعمال فرمول
          </Button>
        </div>
        {error ? <p className="text-sm text-rose-700">{error}</p> : null}
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-700">متغیرهای مجاز</summary>
          <ul className="mt-2 grid gap-1 sm:grid-cols-2">
            {PAYROLL_FORMULA_VARIABLES.map((v) => (
              <li key={v.key}>
                <code className="rounded bg-white px-1 font-mono text-violet-800">{v.key}</code>
                <span className="text-slate-500"> — {v.label}</span>
              </li>
            ))}
          </ul>
        </details>
      </CardContent>
    </Card>
  );
};

export default PayrollFormulaBar;
